import { useCallback, useEffect, useRef, useState } from 'react';

export interface PlayerState {
  playing: boolean;
  muted: boolean;
  currentTime: number;
  duration: number;
  fullscreen: boolean;
  /** True when we are fullscreen but the device stayed portrait, so we rotate in CSS. */
  rotated: boolean;
  /** Overlay controls visibility — auto-hides during playback (YouTube-style). */
  controlsVisible: boolean;
  /** End of the read-ahead buffer around the playhead, in seconds. */
  bufferedTo: number;
  /** Playback has stalled with the buffer empty. */
  buffering: boolean;
}

const SEEK_STEP = 10;
/** How long the controls stay up after the last interaction, while playing. */
const CONTROLS_HIDE_MS = 2800;

/**
 * Two buffered ranges this close together are treated as one: a seek can leave
 * a sub-frame gap that is not an actual interruption in the read-ahead.
 */
const RANGE_GAP_TOLERANCE = 0.5;

/**
 * How far ahead of the playhead the browser has already downloaded. We do not
 * get to choose that distance — with a plain `src` the media stack owns its own
 * read-ahead target — but we can report it, and stay out of its way.
 */
function bufferedEndAt(video: HTMLVideoElement): number {
  const ranges = video.buffered;
  let end = video.currentTime;
  for (let i = 0; i < ranges.length; i += 1) {
    if (ranges.start(i) <= end + RANGE_GAP_TOLERANCE && ranges.end(i) > end) {
      end = ranges.end(i);
    }
  }
  return end;
}

/**
 * `metadata` would leave the buffer empty until the play button is pressed;
 * `auto` lets the download start while the poster is still up, so the first
 * seconds are already on the device. You only reach this screen by choosing a
 * video, so the bytes are wanted — unless the user asked to save data, which
 * over a metered mobile connection outranks a smoother start.
 */
function preloadStrategy(): 'auto' | 'metadata' {
  const connection = (navigator as { connection?: { saveData?: boolean } }).connection;
  return connection?.saveData ? 'metadata' : 'auto';
}

/**
 * ScreenOrientation.lock is not in the standard DOM lib (it is a separate spec
 * and absent on iOS entirely), so it is narrowed here rather than cast inline.
 */
interface LockableOrientation extends ScreenOrientation {
  lock?: (orientation: 'landscape') => Promise<void>;
}

function orientation(): LockableOrientation | undefined {
  return screen.orientation as LockableOrientation | undefined;
}

export function useVideoPlayer(fallbackDuration: number) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(fallbackDuration);
  const [bufferedTo, setBufferedTo] = useState(0);
  const [buffering, setBuffering] = useState(false);
  const [nativeFullscreen, setNativeFullscreen] = useState(false);
  // iOS Safari cannot fullscreen a non-video element, so we fake it with CSS.
  const [fauxFullscreen, setFauxFullscreen] = useState(false);
  const [portrait, setPortrait] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(orientation: portrait)').matches,
  );

  const fullscreen = nativeFullscreen || fauxFullscreen;

  // ---- Controls auto-hide ----
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimerRef = useRef<number | null>(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    const video = videoRef.current;
    // Only auto-hide while actually playing — a paused player keeps its controls.
    if (video && !video.paused) {
      hideTimerRef.current = window.setTimeout(() => setControlsVisible(false), CONTROLS_HIDE_MS);
    }
  }, [clearHideTimer]);

  /** Show the controls and (re)start the inactivity countdown. */
  const revealControls = useCallback(() => {
    setControlsVisible(true);
    scheduleHide();
  }, [scheduleHide]);

  /** Tapping the video surface: hide if shown, reveal if hidden. */
  const toggleControls = useCallback(() => {
    setControlsVisible((visible) => {
      if (visible) {
        clearHideTimer();
        return false;
      }
      scheduleHide();
      return true;
    });
  }, [clearHideTimer, scheduleHide]);

  useEffect(() => clearHideTimer, [clearHideTimer]);

  const syncBuffered = useCallback(() => {
    const video = videoRef.current;
    if (video) setBufferedTo(bufferedEndAt(video));
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTime = () => {
      setCurrentTime(video.currentTime);
      syncBuffered();
    };
    const onMeta = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) setDuration(video.duration);
    };
    // Fires as each chunk lands, including before playback has ever started —
    // that is what makes the buffer visible while the poster is still up.
    const onProgress = () => syncBuffered();
    const onWaiting = () => {
      setBuffering(true);
      syncBuffered();
    };
    // `playing` and `canplay` both mean the buffer refilled; `canplay` also
    // covers a stall that resolved while paused.
    const onResume = () => {
      setBuffering(false);
      syncBuffered();
    };
    // A new src (the admin replaced the file) invalidates everything buffered.
    const onEmptied = () => {
      setBufferedTo(0);
      setBuffering(false);
    };
    // Driven by the element itself, so programmatic play/pause and the OS media
    // controls stay in sync with the icon.
    const onPlay = () => {
      setPlaying(true);
      scheduleHide();
    };
    const onPause = () => {
      setPlaying(false);
      setBuffering(false);
      clearHideTimer();
      setControlsVisible(true);
    };
    const onEnded = () => {
      setPlaying(false);
      setBuffering(false);
      clearHideTimer();
      setControlsVisible(true);
    };
    const onVolume = () => setMuted(video.muted);

    video.addEventListener('timeupdate', onTime);
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('durationchange', onMeta);
    video.addEventListener('progress', onProgress);
    video.addEventListener('seeked', onProgress);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('playing', onResume);
    video.addEventListener('canplay', onResume);
    video.addEventListener('emptied', onEmptied);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('volumechange', onVolume);

    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('durationchange', onMeta);
      video.removeEventListener('progress', onProgress);
      video.removeEventListener('seeked', onProgress);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('playing', onResume);
      video.removeEventListener('canplay', onResume);
      video.removeEventListener('emptied', onEmptied);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('volumechange', onVolume);
    };
  }, [scheduleHide, clearHideTimer, syncBuffered]);

  // The video record loads after this hook mounts, so seed the duration from it
  // rather than showing 00:00 on the scrub bar until loadedmetadata fires.
  useEffect(() => {
    if (fallbackDuration > 0) setDuration((current) => (current > 0 ? current : fallbackDuration));
  }, [fallbackDuration]);

  useEffect(() => {
    const onFsChange = () => setNativeFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    const query = window.matchMedia('(orientation: portrait)');
    const onChange = (event: MediaQueryListEvent) => setPortrait(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play().catch(() => setPlaying(false));
    } else {
      video.pause();
    }
  }, []);

  const seekBy = useCallback((delta: number) => {
    const video = videoRef.current;
    if (!video) return;
    const max = Number.isFinite(video.duration) ? video.duration : video.currentTime + delta;
    video.currentTime = Math.min(Math.max(video.currentTime + delta, 0), max);
    setCurrentTime(video.currentTime);
  }, []);

  const seekTo = useCallback((time: number) => {
    const video = videoRef.current;
    if (!video) return;
    const max = Number.isFinite(video.duration) ? video.duration : time;
    video.currentTime = Math.min(Math.max(time, 0), max);
    setCurrentTime(video.currentTime);
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const container = containerRef.current;
    if (!container) return;

    if (fullscreen) {
      if (document.fullscreenElement) {
        await document.exitFullscreen().catch(() => {});
      }
      // Releasing the lock lets the device follow its own rotation again.
      try {
        orientation()?.unlock?.();
      } catch {
        /* unsupported */
      }
      setFauxFullscreen(false);
      return;
    }

    if (container.requestFullscreen) {
      try {
        await container.requestFullscreen();
        // Not supported on iOS; the CSS rotation below covers that case.
        await orientation()?.lock?.('landscape').catch(() => {});
        return;
      } catch {
        /* fall through to the CSS approach */
      }
    }
    setFauxFullscreen(true);
  }, [fullscreen]);

  const state: PlayerState = {
    playing,
    muted,
    currentTime,
    duration,
    fullscreen,
    rotated: fullscreen && portrait,
    controlsVisible,
    bufferedTo,
    buffering,
  };

  return {
    videoRef,
    containerRef,
    state,
    preload: preloadStrategy(),
    togglePlay,
    toggleMute,
    toggleFullscreen,
    seekTo,
    seekBack: () => seekBy(-SEEK_STEP),
    seekForward: () => seekBy(SEEK_STEP),
    revealControls,
    toggleControls,
  };
}
